import React from "react";
import cx from "classnames";
import throttle from "lodash/throttle";
import { useResizeObserver } from "./hooks/useResizeObserver";
import "./Clock.css";

interface IProps {
  editable?: boolean;
  duration: number;
  onChangeDuration: (progress: number) => void;
}

const HOUR_IN_MILLISECONDS = 1000 * 60 * 60;
const SECOND_IN_MILLISECONDS = 1000;

const THROTTLE_WAIT = 50;

const SVG_CLOCK_STROKE_WIDTH = 1;
const SVG_HAND_STROKE_WIDTH = 1;
const SVG_HAND_CIRCLE_RADIUS = 2;
const SVG_PROGRESS_RADIUS = 16 - SVG_HAND_CIRCLE_RADIUS - SVG_CLOCK_STROKE_WIDTH / 2;

const isTouchDevice = "ontouchstart" in window;

function getRadAngleFromYAxisToLine({ x, y }: { x: number; y: number }): number {
  if (x > 0 && y === 0) return Math.PI / 2;
  if (x < 0 && y === 0) return 1.5 * Math.PI;
  if (x === 0 && y > 0) return 0;
  if (x === 0 && y < 0) return Math.PI;

  if (x === 0 && y === 0) return 0;

  const angle = Math.atan(Math.abs(y / x));

  if (x > 0 && y > 0) return Math.PI / 2 - angle;
  if (x > 0 && y < 0) return Math.PI / 2 + angle;
  if (x < 0 && y > 0) return 1.5 * Math.PI + angle;
  if (x < 0 && y < 0) return 1.5 * Math.PI - angle;

  throw new Error("Unhandled condition");
}

export function Clock(props: IProps) {
  const { editable = false, duration, onChangeDuration } = props;
  const isPressingRef = React.useRef(false);
  const { ref, width = 0, height = 0, left = 0, top = 0 } = useResizeObserver<HTMLDivElement>();
  const progress = duration / HOUR_IN_MILLISECONDS;

  const handlePressDraggingHand = () => {
    isPressingRef.current = true;
  };

  React.useEffect(() => {
    if (!editable) return () => {};

    function handleReleaseDraggingHand() {
      isPressingRef.current = false;
    }

    if (isTouchDevice) {
      document.addEventListener("touchcancel", handleReleaseDraggingHand);
      document.addEventListener("touchend", handleReleaseDraggingHand);
      return () => {
        document.removeEventListener("touchcancel", handleReleaseDraggingHand);
        document.removeEventListener("touchend", handleReleaseDraggingHand);
      };
    }

    document.addEventListener("mouseup", handleReleaseDraggingHand);
    return () => {
      document.removeEventListener("mouseup", handleReleaseDraggingHand);
    };
  }, [editable]);

  React.useEffect(() => {
    if (!editable) return () => {};

    const updatePercentageNumber = ({ clientX, clientY }: { clientX: number; clientY: number }) => {
      const xRelativeToCenter = clientX - left - width / 2;
      const yRelativeToCenter = -(clientY - top - height / 2);

      // Exact center of the clock
      if (xRelativeToCenter === 0 && yRelativeToCenter === 0) {
        return;
      }

      const arcRadAngle = getRadAngleFromYAxisToLine({ x: xRelativeToCenter, y: yRelativeToCenter });
      const progressRatio = arcRadAngle / (2 * Math.PI);

      const duration = progressRatio * HOUR_IN_MILLISECONDS;
      const durationWithoutMilliseconds = duration - (duration % SECOND_IN_MILLISECONDS);

      onChangeDuration(durationWithoutMilliseconds);
    };

    if (isTouchDevice) {
      const handleTouchMove = (event: TouchEvent) => {
        if (isPressingRef.current) {
          const touch = event.touches.item(0);
          if (touch == null) return;
          const { clientX, clientY } = touch;
          updatePercentageNumber({ clientX, clientY });
        }
      };

      const throttledTouchMove = throttle(handleTouchMove, THROTTLE_WAIT);
      document.addEventListener("touchmove", throttledTouchMove);
      return () => {
        throttledTouchMove.cancel();
        document.removeEventListener("touchmove", throttledTouchMove);
      };
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (isPressingRef.current) {
        const { clientX, clientY } = event;
        updatePercentageNumber({ clientX, clientY });
      }
    };
    const throttledMouseMove = throttle(handleMouseMove, THROTTLE_WAIT);
    document.addEventListener("mousemove", throttledMouseMove);
    return () => {
      throttledMouseMove.cancel();
      document.removeEventListener("mousemove", throttledMouseMove);
    };
  }, [editable, height, left, top, width, onChangeDuration]);

  function getHandHandlers() {
    if (!editable) {
      return undefined;
    }
    return isTouchDevice
      ? {
          onTouchStart: handlePressDraggingHand,
        }
      : {
          onMouseDown: handlePressDraggingHand,
        };
  }

  return (
    <div ref={ref}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="Clock__shape">
        <circle
          fill="none"
          cx="16"
          cy="16"
          r={SVG_PROGRESS_RADIUS}
          stroke="var(--secondary-color)"
          strokeWidth={SVG_CLOCK_STROKE_WIDTH}
        />
        <circle
          fill="none"
          transform="rotate(-90)"
          className={cx("Clock__progress", {
            "Clock__progress--animated": true,
          })}
          cx="16"
          cy="16"
          r={SVG_PROGRESS_RADIUS}
          stroke="var(--primary-color)"
          strokeWidth={SVG_CLOCK_STROKE_WIDTH}
          strokeDasharray={2 * Math.PI * SVG_PROGRESS_RADIUS}
          strokeDashoffset={2 * Math.PI * SVG_PROGRESS_RADIUS * (1 - progress)}
        />
        <g transform="translate(-201 -1495.362)">
          <path
            fill="none"
            stroke="var(--secondary-color)"
            d="M217.00001 1524.8554l0-2 M203.50712 1511.3622l2 0 M217.00935 1497.8753l0 2M230.49625 1511.3775l-2 0M205.32203 1518.1112l1.73205-1M210.26103 1499.6794l1 1.732M228.69215 1504.6292l-1.73205 1M223.74235 1523.0603l-1-1.7321M205.32368 1504.6256l1.73205 1M223.75559 1499.687l-.99996 1.7321M228.6845 1518.1237l-1.73205-1 M210.2478 1523.0526l1.00004-1.732"
          />
        </g>
        <g
          {...getHandHandlers()}
          className={cx("Clock__hand", {
            "Clock__hand--disabled": !editable,
            "Clock__hand--animated": true,
          })}
          style={{
            transform: `rotate(${360 * progress}deg)`,
          }}>
          <line
            x1="16"
            x2="16"
            y1="16"
            y2={SVG_HAND_CIRCLE_RADIUS + SVG_HAND_STROKE_WIDTH}
            stroke="var(--third-color)"
            strokeWidth={SVG_HAND_STROKE_WIDTH}
          />
          <circle cx="16" cy={SVG_HAND_CIRCLE_RADIUS} r={SVG_HAND_CIRCLE_RADIUS} fill="var(--third-color)" />
        </g>
      </svg>
    </div>
  );
}
